import React, { useState } from 'react';
import { Key, Copy, RotateCcw, Trash2, Plus, Activity, TrendingUp } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Loading } from '../../components/ui/Loading';
import { EmptyState } from '../../components/ui/EmptyState';
import { useApiKeys, useCreateApiKey, useRotateApiKey, useDeleteApiKey, useSdkUsage } from '../../hooks/useApiKeys';
import { formatDate, copyToClipboard } from '../../lib/utils';
import toast from 'react-hot-toast';

const ApiKeys: React.FC = () => {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newKey, setNewKey] = useState<string | null>(null);
    const [keyCopied, setKeyCopied] = useState(false);
    const [isRotation, setIsRotation] = useState(false);
    const { data: apiKeys, isLoading } = useApiKeys();
    const { data: sdkUsage } = useSdkUsage();
    const createMutation = useCreateApiKey();
    const rotateMutation = useRotateApiKey();
    const deleteMutation = useDeleteApiKey();

    const [formData, setFormData] = useState({
        name: '',
        environment: 'test' as 'production' | 'test',
        ip_whitelist: '',
    });

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();

        const ipList = formData.ip_whitelist
            .split(',')
            .map((ip) => ip.trim())
            .filter((ip) => ip.length > 0);

        createMutation.mutate(
            {
                name: formData.name,
                environment: formData.environment,
                ip_whitelist: ipList.length > 0 ? ipList : undefined,
            },
            {
                onSuccess: (data) => {
                    setNewKey(data.key || null);
                    setKeyCopied(false);
                    setIsRotation(false);
                    setFormData({ name: '', environment: 'test', ip_whitelist: '' });
                },
            }
        );
    };

    const handleCopy = async (text: string) => {
        const success = await copyToClipboard(text);
        if (success) {
            toast.success('Copied to clipboard!');
            if (text === newKey) {
                setKeyCopied(true);
            }
        }
    };

    const handleRotate = async (keyId: string, keyName: string) => {
        if (window.confirm(`⚠️ Are you sure you want to rotate "${keyName}"?\n\nThe old key will stop working IMMEDIATELY!\nMake sure you're ready to update your integrations.`)) {
            rotateMutation.mutate(keyId, {
                onSuccess: (data) => {
                    setNewKey(data.key || null);
                    setKeyCopied(false);
                    setIsRotation(true);
                    setShowCreateModal(true);
                },
            });
        }
    };

    const handleDelete = async (keyId: string) => {
        if (window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
            deleteMutation.mutate(keyId);
        }
    };

    if (isLoading) {
        return <Loading fullScreen message="Loading API keys..." />;
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
                    <p className="text-gray-600 mt-1">Manage your integration API keys</p>
                </div>
                <button onClick={() => setShowCreateModal(true)} className="btn-primary flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    Create New Key
                </button>
            </div>

            {/* SDK Usage Overview */}
            {sdkUsage && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <Activity className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">This Minute</p>
                                <p className="text-2xl font-bold text-gray-900">{sdkUsage.summary.usage.this_minute}</p>
                                <p className="text-xs text-gray-500">requests</p>
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-green-100 rounded-lg">
                                <TrendingUp className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Today</p>
                                <p className="text-2xl font-bold text-gray-900">{sdkUsage.summary.usage.today.toLocaleString()}</p>
                                <p className="text-xs text-gray-500">requests</p>
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-purple-100 rounded-lg">
                                <TrendingUp className="w-6 h-6 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">This Month</p>
                                <p className="text-2xl font-bold text-gray-900">{sdkUsage.summary.usage.this_month.toLocaleString()}</p>
                                <p className="text-xs text-gray-500">requests</p>
                            </div>
                        </div>
                    </Card>
                </div>
            )}

            {/* API Keys List */}
            {apiKeys && apiKeys.length > 0 ? (
                <div className="space-y-4">
                    {apiKeys.map((key) => {
                        const keyUsage = sdkUsage?.api_keys.find(k => k.api_key_id === key.id);

                        return (
                            <Card key={key.id}>
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <Key className="w-5 h-5 text-gray-400" />
                                            <h3 className="text-lg font-semibold text-gray-900">{key.name}</h3>
                                            <Badge className={key.environment === 'production' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                                                {key.environment}
                                            </Badge>
                                            <Badge className="bg-gray-100 text-gray-700">
                                                Unlimited Requests
                                            </Badge>
                                        </div>

                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <code className="text-sm font-mono bg-gray-100 px-3 py-1 rounded">{key.key_prefix}</code>
                                                <button
                                                    onClick={() => handleCopy(key.key_prefix)}
                                                    className="p-1 hover:bg-gray-100 rounded"
                                                    title="Copy"
                                                >
                                                    <Copy className="w-4 h-4 text-gray-600" />
                                                </button>
                                            </div>

                                            {keyUsage && (
                                                <div className="flex items-center gap-4 text-sm">
                                                    <span className="text-gray-500">Usage:</span>
                                                    <span className="text-gray-700">
                                                        <strong>{keyUsage.usage.this_minute}</strong> this minute
                                                    </span>
                                                    <span className="text-gray-700">
                                                        <strong>{keyUsage.usage.today.toLocaleString()}</strong> today
                                                    </span>
                                                    <span className="text-gray-700">
                                                        <strong>{keyUsage.usage.this_month.toLocaleString()}</strong> this month
                                                    </span>
                                                </div>
                                            )}

                                            {key.ip_whitelist && key.ip_whitelist.length > 0 && (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm text-gray-500">IP Whitelist:</span>
                                                    <span className="text-sm text-gray-700">{key.ip_whitelist.join(', ')}</span>
                                                </div>
                                            )}

                                            <div className="flex items-center gap-4 text-sm text-gray-500">
                                                <span>Created: {formatDate(key.created_at)}</span>
                                                {keyUsage?.last_request_at && (
                                                    <span>Last used: {formatDate(keyUsage.last_request_at)}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleRotate(key.id, key.name)}
                                            className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                                            title="Rotate Key"
                                        >
                                            <RotateCcw className="w-5 h-5 text-blue-600" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(key.id)}
                                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Delete Key"
                                        >
                                            <Trash2 className="w-5 h-5 text-red-600" />
                                        </button>
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            ) : (
                <Card>
                    <EmptyState
                        icon={Key}
                        title="No API keys yet"
                        description="Create your first API key to start integrating with silentloss."
                        action={{
                            label: 'Create API Key',
                            onClick: () => setShowCreateModal(true),
                        }}
                    />
                </Card>
            )}

            {/* Create/Show Key Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        {newKey ? (
                            <>
                                <h2 className="text-xl font-bold text-gray-900 mb-4">
                                    {isRotation ? '🔄 API Key Rotated Successfully!' : '✅ API Key Created Successfully!'}
                                </h2>

                                {isRotation && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                                        <p className="text-sm text-red-800 font-medium mb-2">
                                            ⚠️ <strong>Important:</strong> The old key has been deactivated!
                                        </p>
                                        <p className="text-xs text-red-700">
                                            Update your integrations immediately to prevent service interruptions.
                                        </p>
                                    </div>
                                )}

                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                                    <p className="text-sm text-yellow-800 font-medium mb-1">
                                        🔐 Save this key now!
                                    </p>
                                    <p className="text-xs text-yellow-700">
                                        For security reasons, this is the only time you'll see the full key.
                                    </p>
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Your New API Key:</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={newKey}
                                            readOnly
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm"
                                        />
                                        <button
                                            onClick={() => handleCopy(newKey)}
                                            className={`p-2 rounded-lg transition-colors ${keyCopied
                                                ? 'bg-green-600 hover:bg-green-700'
                                                : 'bg-primary-600 hover:bg-primary-700'
                                                } text-white`}
                                            title={keyCopied ? 'Copied!' : 'Copy to clipboard'}
                                        >
                                            <Copy className="w-5 h-5" />
                                        </button>
                                    </div>
                                    {keyCopied && (
                                        <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                                            ✓ Key copied to clipboard!
                                        </p>
                                    )}
                                </div>

                                <button
                                    onClick={() => {
                                        if (!keyCopied) {
                                            if (!window.confirm('⚠️ You haven\'t copied the key yet!\n\nAre you sure you want to close? You won\'t be able to see it again.')) {
                                                return;
                                            }
                                        }
                                        setNewKey(null);
                                        setShowCreateModal(false);
                                        setKeyCopied(false);
                                        setIsRotation(false);
                                    }}
                                    className={`w-full ${keyCopied ? 'btn-primary' : 'bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors'}`}
                                >
                                    {keyCopied ? 'Done' : 'Close (Not Copied Yet!)'}
                                </button>
                            </>
                        ) : (
                            <>
                                <h2 className="text-xl font-bold text-gray-900 mb-4">Create API Key</h2>
                                <form onSubmit={handleCreate} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Key Name</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                                            className="input"
                                            placeholder="Production API Key"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Environment</label>
                                        <select
                                            value={formData.environment}
                                            onChange={(e) => setFormData((prev) => ({ ...prev, environment: e.target.value as 'production' | 'test' }))}
                                            className="input"
                                        >
                                            <option value="test">Test</option>
                                            <option value="production">Production</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            IP Whitelist (Optional)
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.ip_whitelist}
                                            onChange={(e) => setFormData((prev) => ({ ...prev, ip_whitelist: e.target.value }))}
                                            className="input"
                                            placeholder="192.168.1.1, 10.0.0.1"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Comma-separated IP addresses</p>
                                    </div>

                                    <div className="flex gap-3">
                                        <button type="submit" disabled={createMutation.isPending} className="flex-1 btn-primary">
                                            {createMutation.isPending ? 'Creating...' : 'Create Key'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setShowCreateModal(false)}
                                            className="flex-1 btn-secondary"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApiKeys;
